/**
 * `@akademi/site-builder-blocks` — generic UI block components for the
 * portal renderer. Each block is host-agnostic: pure presentational,
 * reads design tokens from CSS variables (set by
 * {@code @akademi/site-builder-runtime}'s {@code ThemeTokens}), no data
 * fetching, no LMS coupling.
 *
 * <h3>Usage</h3>
 *
 * <pre>{@code
 * import { createBlockResolver, BlockResolverProvider } from '@akademi/site-builder-runtime';
 * import { genericBlocks } from '@akademi/site-builder-blocks';
 *
 * const resolver = createBlockResolver({ defaultBlocks: genericBlocks });
 *
 * function Root({ children }) {
 *   return <BlockResolverProvider value={resolver}>{children}</BlockResolverProvider>;
 * }
 * }</pre>
 *
 * <h3>What ships</h3>
 *
 * Ported from {@code ThemeMaker/lms-portal/src/components/blocks/} —
 * the generic categories. Module-specific blocks (blog, wiki, video,
 * micro) ship in their own packages; LMS-specific blocks live in the
 * host app.
 */

// — Components ——————————————————————————————————————————————————————
// Hero variants
// Shared primitives (re-exported for use by host-app LMS-specific blocks)

export { HeroBanner, type HeroBannerProps } from './hero/HeroBanner';
export { HeroSplit, type HeroSplitProps } from './hero/HeroSplit';
export { HeroEditorial, type HeroEditorialProps } from './hero/HeroEditorial';
export { HeroSlider, type HeroSliderProps, type HeroSliderSlide } from './hero/HeroSlider';
export {
  HeroCinematic,
  type HeroCinematicProps,
  type HeroCinematicSlide,
} from './hero/HeroCinematic';
export { HeroSpotlightCards, type HeroSpotlightCardsProps } from './hero/HeroSpotlightCards';
export { HeroSpotlight, type HeroSpotlightProps } from './hero/HeroSpotlight';
export { VideoPlayerHero, type VideoPlayerHeroProps } from './hero/VideoPlayerHero';
export {
  HeroFeatureRail,
  type HeroFeatureRailProps,
  type HeroFeatureRailSlide,
  type HeroFeatureRailItem,
} from './hero/HeroFeatureRail';
export { HeroCarousel, type HeroCarouselProps, type HeroCarouselSlide } from './hero/HeroCarousel';
export {
  HeroCategoryBanner,
  type HeroCategoryBannerProps,
  type HeroCategoryBannerSlide,
} from './hero/HeroCategoryBanner';
export {
  HeroDualBanner,
  type HeroDualBannerProps,
  type HeroDualBannerSlide,
} from './hero/HeroDualBanner';
export {
  HeroSplitRotator,
  type HeroSplitRotatorProps,
  type HeroSplitRotatorButton,
  type HeroSplitRotatorItem,
  type HeroSplitRotatorFeature,
  type HeroSplitRotatorType,
} from './hero/HeroSplitRotator';
export { HeroSignalDeck, type HeroSignalDeckProps } from './hero/HeroSignalDeck';
export {
  HeroTriColumn,
  type HeroTriColumnProps,
  type HeroTriColumnFeature,
  type HeroTriColumnTone,
  type HeroTriColumnCta,
  type HeroTriColumnIconStyle,
  type HeroTriColumnImageSlot,
} from './hero/HeroTriColumn';
export {
  HeroPromoRail,
  type HeroPromoRailProps,
  type HeroPromoRailCard,
  type HeroPromoRailVariant,
  type HeroPromoRailCta,
  type HeroPromoRailTone,
} from './hero/HeroPromoRail';
export {
  HeroPlaylistRail,
  type HeroPlaylistRailProps,
  type HeroPlaylistRailItem,
  type HeroPlaylistRailStat,
} from './hero/HeroPlaylistRail';
export {
  HeroFullHeightBanner,
  type HeroFullHeightBannerProps,
  type HeroFullHeightBannerSlide,
  type HeroFullHeightBannerCardPosition,
  type HeroFullHeightBannerImagePosition,
} from './hero/HeroFullHeightBanner';
export {
  PluralsightHero,
  type PluralsightHeroProps,
  type PluralsightHeroCard,
} from './hero/PluralsightHero';
export {
  WildTvHero,
  type WildTvHeroProps,
  type WildTvHeroNavItem,
  type WildTvHeroEpisode,
} from './hero/WildTvHero';

// Text + FAQ
export { RichText, type RichTextProps } from './text/RichText';
export { HtmlBlock, type HtmlBlockProps } from './html/HtmlBlock';
export { FaqAccordion, type FaqAccordionProps } from './text/FaqAccordion';

// Activity / events
export { ActivityRow, type ActivityRowProps } from './activity/ActivityRow';
export { DynamicFormBlock, type DynamicFormBlockProps } from './form/DynamicFormBlock';
export {
  DynamicFormRenderer,
  type DynamicFormRendererProps,
  type ValidationLabels,
} from './form/DynamicFormRenderer';
export { ActivityBlock, type ActivityBlockProps } from './activity/ActivityBlock';
export { ActivityBlockBtk, type ActivityBlockBtkProps } from './activity/ActivityBlockBtk';
export {
  ActivityApplicationModal,
  type ActivityApplicationModalProps,
  type ActivityApplicationLabels,
} from './activity/ActivityApplicationModal';

// CTAs
export { CtaBand, type CtaBandProps } from './cta/CtaBand';
export { CtaCardModern, type CtaCardModernProps } from './cta/CtaCardModern';
export { SubscribeCard, type SubscribeCardProps } from './cta/SubscribeCard';

// Media
export { Image, type ImageProps } from './media/Image';
export { MediaCardRow, type MediaCardRowProps, type MediaCardAspect } from './media/MediaCardRow';
export { MediaDetailModal, type MediaDetailModalProps } from './media/MediaDetailModal';
export { Podcasts, type PodcastsProps } from './media/Podcasts';
export {
  FullHeightPodcast,
  type FullHeightPodcastProps,
  type FullHeightPodcastButton,
  type FullHeightPodcastPlatform,
  type FullHeightPodcastPlatformKind,
  type FullHeightPodcastEpisode,
} from './media/FullHeightPodcast';

// Layout primitives — composable containers
export { Band, type BandProps } from './layout/Band';
export { BackgroundGlow, type BackgroundGlowProps } from './layout/BackgroundGlow';
export { Columns, type ColumnsProps } from './layout/Columns';
export { Container, type ContainerProps } from './layout/Container';
export { Divider, type DividerProps } from './layout/Divider';
export { Spacer, type SpacerProps } from './layout/Spacer';

// Grids
export { FeatureGrid, type FeatureGridProps } from './grid/FeatureGrid';
export { PartnersGrid, type PartnersGridProps } from './grid/PartnersGrid';
export { CategoryGrid, type CategoryGridProps, type CategoryGridItem } from './grid/CategoryGrid';
export { PillarStack, type PillarStackProps, type PillarStackCard } from './grid/PillarStack';
export {
  PluralsightOutcomeGrid,
  type PluralsightOutcomeGridProps,
} from './grid/PluralsightOutcomeGrid';
export { PluralsightTopicGrid, type PluralsightTopicGridProps } from './grid/PluralsightTopicGrid';

// Money — the decimal-safe formatter the marketplace blocks price with, exported
// so a host's own blocks (a theme's, or an app's shop blocks) format money the
// same way instead of reimplementing it.
export { formatMoney, formatMoneyOrUndefined, formatDecimalMoney } from './_shared/formatMoney';

// Marketplace — akademi-lms-v2's course-catalog/cart/checkout funnel,
// dual-mode: real data through `useMarketplaceSource()` when a host mounts
// it, authored `items`/props otherwise. UI styled after akademi-sem. Distinct
// from a physical-product shop's blocks, which a host that sells goods brings
// itself and feeds through `useCommerceSource()`.
export {
  MarketplaceCatalogGrid,
  type MarketplaceCatalogGridProps,
} from './marketplace/MarketplaceCatalogGrid';
export {
  MarketplaceCourseGrid,
  type MarketplaceCourseGridProps,
} from './marketplace/MarketplaceCourseGrid';
export {
  MarketplaceCoursePurchaseBox,
  type MarketplaceCoursePurchaseBoxProps,
} from './marketplace/MarketplaceCoursePurchaseBox';
export {
  MarketplaceCatalogHero,
  type MarketplaceCatalogHeroProps,
} from './marketplace/MarketplaceCatalogHero';
export {
  MarketplaceCartItems,
  type MarketplaceCartItemsProps,
} from './marketplace/MarketplaceCartItems';
export {
  MarketplaceCartSummary,
  type MarketplaceCartSummaryProps,
} from './marketplace/MarketplaceCartSummary';
export {
  MarketplaceCouponCode,
  type MarketplaceCouponCodeProps,
} from './marketplace/MarketplaceCouponCode';
export {
  MarketplaceAddressSelect,
  type MarketplaceAddressSelectProps,
} from './marketplace/MarketplaceAddressSelect';
export {
  MarketplacePaymentMethod,
  type MarketplacePaymentMethodProps,
} from './marketplace/MarketplacePaymentMethod';
export {
  MarketplaceCheckoutCta,
  type MarketplaceCheckoutCtaProps,
} from './marketplace/MarketplaceCheckoutCta';
export {
  MarketplaceCheckoutStepper,
  type MarketplaceCheckoutStepperProps,
} from './marketplace/MarketplaceCheckoutStepper';
export {
  MarketplacePendingOrders,
  type MarketplacePendingOrdersProps,
} from './marketplace/MarketplacePendingOrders';
export {
  MarketplaceBankTransferInstructions,
  type MarketplaceBankTransferInstructionsProps,
} from './marketplace/MarketplaceBankTransferInstructions';
export { MarketplaceCard, type MarketplaceCardData } from './marketplace/_MarketplaceCard';
export { type MarketplaceCatalogItem } from './marketplace/MarketplaceCatalogGrid';
export { type MarketplaceCourseItem } from './marketplace/MarketplaceCourseGrid';
export { formatMarketPrice, type MarketPrice } from './marketplace/_shared';

// Stats
export { MetricRow, type MetricRowProps } from './stats/MetricRow';
export {
  FullHeightStats,
  type FullHeightStatsProps,
  type FullHeightStatsSlide,
  type FullHeightStatsItem,
} from './stats/FullHeightStats';
export { PluralsightProofBand, type PluralsightProofBandProps } from './stats/PluralsightProofBand';

// Gamification
export { GamificationPanel, type GamificationPanelProps } from './gamification/GamificationPanel';
export {
  GamificationPanelV2,
  type GamificationPanelV2Props,
} from './gamification/GamificationPanelV2';
export {
  GamificationPanelV3,
  type GamificationPanelV3Props,
} from './gamification/GamificationPanelV3';
export { FullBadge, type FullBadgeProps } from './gamification/FullBadge';

// Promo
export { ComingSoon, type ComingSoonProps, type ComingSoonItem } from './promo/ComingSoon';
export { AdBanner, type AdBannerProps } from './promo/AdBanner';
export {
  MobileAppPromo,
  type MobileAppPromoProps,
  type MobileAppPromoAccent,
  type MobileAppPromoImagePosition,
} from './promo/MobileAppPromo';
export { MobileAppShowcase, type MobileAppShowcaseProps } from './promo/MobileAppShowcase';
export {
  FullHeightMobilePromo,
  type FullHeightMobilePromoProps,
} from './promo/FullHeightMobilePromo';

// Tabs
export { ButtonGroup, type ButtonGroupProps, type ButtonGroupItem } from './tabs/ButtonGroup';

// Nav
export { PlansPricing, type PlansPricingProps } from './nav/PlansPricing';
export { CategoryMenu, type CategoryMenuProps, type CategoryMenuItem } from './nav/CategoryMenu';

// Shared level badge — theme-token-colored, used across cards + course detail
export {
  LevelBadge,
  normalizeLevelCode,
  type LevelBadgeProps,
  type LevelCode,
} from './_shared/LevelBadge';

// Shared star-rating display + interactive picker — amber, theme-token-colored,
// used across cards + course detail (display) and the comment composer (input).
export { Stars, StarRating, type StarsProps, type StarRatingProps } from './_shared/Stars';

// Course-card variants (host-supplied data → tile UI)
export type { CourseCardProps } from './cards/CourseCard.types';
export { CourseCardImage } from './cards/CourseCardImage';
export {
  type CardGridSpec,
  DEFAULT_GRID_SPEC,
  SERVER_SAFE_DEFAULT_GRID_SPEC,
  getGridSpec,
} from './cards/gridSpecs';
export { DefaultCourseCard } from './cards/DefaultCourseCard';
export { CompactCourseCard } from './cards/CompactCourseCard';
export { WideCourseCard } from './cards/WideCourseCard';
export { MinimalCourseCard } from './cards/MinimalCourseCard';
export { CinematicCourseCard } from './cards/CinematicCourseCard';
export { MarketplaceCourseCard } from './cards/MarketplaceCourseCard';
export { AcademicCourseCard } from './cards/AcademicCourseCard';
export { ClassicHighCourseCard } from './cards/ClassicHighCourseCard';
export { ClassicNetflixZoomCourseCard } from './cards/ClassicNetflixZoomCourseCard';
export {
  resolveCourseCard,
  type CourseCardComponent,
  type CourseCardVariant,
} from './cards/registry';

// — UI primitives (composable scaffolding) ——————————————————————————————
export { Donut, type DonutProps, type DonutSegment } from './ui/Donut';
export { Tabs, type TabsProps } from './ui/Tabs';
export {
  ContentRail,
  type ContentRailProps,
  type ContentRailSnap,
  type ContentRailArrows,
} from './ui/ContentRail';
export { CollapsibleDescription } from './ui/CollapsibleDescription';

// — Misc / proof / journeys / embeds / video hero ——————————————————————
export { AcmePartnerBadge, type AcmePartnerBadgeProps } from './misc/AcmePartnerBadge';
export { Testimonials, type TestimonialsProps } from './proof/Testimonials';
export { TestimonialsBento, type TestimonialsBentoProps } from './proof/TestimonialsBento';
export {
  LearningJourneys as GenericLearningJourneys,
  type LearningJourneysProps as GenericLearningJourneysProps,
} from './journeys/LearningJourneys';
export {
  LearningJourneyPlans,
  type LearningJourneyPlansProps,
} from './journeys/LearningJourneyPlans';
export { HeroVideo, type HeroVideoProps } from './hero-video/HeroVideo';
export {
  HeroVideoBackground,
  HeroVideoBackgroundHero,
  type HeroVideoBackgroundHeroProps,
} from './hero-video/HeroVideoBackground';

// Calendar
export { CalendarBox, type CalendarBoxProps } from './calendar/CalendarBox';
export { LiveCalendarBox, type LiveCalendarBoxProps } from './calendar/LiveCalendarBox';

// Theme-specific blocks (BTK, etc.) ship in their own theme packages
// (`@akademi/theme-<name>`), NOT here.

// — Third-party embeds (Calendly, Typeform, HubSpot, video) ——————————————
export {
  EmbedCard,
  LoadingState,
  ConfigurationError,
  useStableId,
  useExternalScript,
} from './embeds/_shared';
export { CalendlyBlock, type CalendlyBlockProps } from './embeds/CalendlyBlock';
export { TypeformBlock, type TypeformBlockProps } from './embeds/TypeformBlock';
export {
  HubSpotMeetingsBlock,
  type HubSpotMeetingsBlockProps,
} from './embeds/HubSpotMeetingsBlock';
export { HubSpotFormBlock, type HubSpotFormBlockProps } from './embeds/HubSpotFormBlock';
export { EmbeddedVideo, type EmbeddedVideoProps } from './embeds/EmbeddedVideo';

// — Schema.org JSON-LD generators ————————————————————————————————————————
export { JsonLdScript, stripUndefined } from './schema/_shared';
export {
  BreadcrumbListSchemaJsonLd,
  type BreadcrumbListSchemaJsonLdProps,
  type BreadcrumbItem,
} from './schema/BreadcrumbListSchemaJsonLd';
export {
  OrganizationSchemaJsonLd,
  buildOrganizationSchema,
  type OrganizationSchemaJsonLdProps,
} from './schema/OrganizationSchemaJsonLd';
export {
  WebSiteSchemaJsonLd,
  buildWebSiteSchema,
  type WebSiteSchemaJsonLdProps,
} from './schema/WebSiteSchemaJsonLd';
export {
  PersonSchemaJsonLd,
  buildPersonSchema,
  type PersonSchemaJsonLdProps,
  type PersonSchemaInput,
} from './schema/PersonSchemaJsonLd';
export {
  AggregateRatingSchemaJsonLd,
  buildAggregateRatingSchema,
  type AggregateRatingSchemaJsonLdProps,
  type AggregateRatingSchemaInput,
} from './schema/AggregateRatingSchemaJsonLd';
export {
  ItemListSchemaJsonLd,
  buildItemListSchema,
  type ItemListSchemaJsonLdProps,
  type ItemListEntry,
} from './schema/ItemListSchemaJsonLd';
export {
  ArticleSchemaJsonLd,
  buildArticleSchema,
  type ArticleSchemaJsonLdProps,
  type ArticleType,
} from './schema/ArticleSchemaJsonLd';
export {
  LearningResourceSchemaJsonLd,
  buildLearningResourceSchema,
  type LearningResourceSchemaJsonLdProps,
  type LearningResourceType,
  type EducationalLevel,
} from './schema/LearningResourceSchemaJsonLd';
export {
  CourseSchemaJsonLd,
  buildCourseSchema,
  type CourseSchemaJsonLdProps,
  type CourseSchemaInput,
} from './schema/CourseSchemaJsonLd';
export {
  VideoObjectSchemaJsonLd,
  buildVideoObjectSchema,
  type VideoObjectSchemaJsonLdProps,
  type VideoSchemaInput,
} from './schema/VideoObjectSchemaJsonLd';

// — Shared helpers (exported for hosts that want to compose) ———————————
export { tx, asList, asNumber, asBool, asObject, pickEnum } from './_shared/coerce';
export { sectionPad, muted, textSecondary } from './_shared/styles';
export { SectionShell } from './_shared/SectionShell';
export { BlockRoot, type BlockRootProps } from './_shared/BlockRoot';
export { BLOCK_GROUPS } from './blockGroups.generated';
export { HeadingLine, HEADING_LINE_CLASS } from './_shared/HeadingLine';
export { EmptyState, type EmptyStateProps } from './_shared/EmptyState';
export { StaleIndicator } from './_shared/StaleIndicator';
export { SkeletonRow } from './_shared/SkeletonRow';
export { ignoreSbSelectPortalOutsideClick } from './_shared/dialogOutsideClick';
export { AdapterPlaceholder } from './_shared/AdapterPlaceholder';
export { useAsyncResource, type AsyncResourceState } from './_shared/useAsyncResource';
export {
  useLearningRow,
  type LearningRowState,
  type UseLearningRowOpts,
} from './_shared/useLearningRow';
export {
  useContentRow,
  type ContentRowState,
  type UseContentRowOpts,
} from './_shared/useContentRow';
export { prettify } from './_shared/prettify';
export * as sharedStyles from './_shared/styles';

// — Course-detail kit (3 variants + shared primitives) ————————————————
export * from './course-detail';
export * as courseDetailShared from './course-detail/shared';

// — Article card primitives ———————————————————————————————————————————
export {
  resolveArticleCard,
  ArticleCardImageLed,
  ArticleCardMinimal,
  ArticleCardMagazine,
  type ArticleCardProps,
  type ArticleCardVariant,
} from './cards/ArticleCard';
export { learningItemToCardProps } from './cards/learningItemToCardProps';

// — Blog blocks (BlogSource adapter) ——————————————————————————————————
export { BlogArticleGrid, type BlogArticleGridProps } from './blog/BlogArticleGrid';
export { BlogFeaturedHero, type BlogFeaturedHeroProps } from './blog/BlogFeaturedHero';
export {
  resolveBlogHero,
  BlogHeroGradient,
  BlogHeroCoverPhoto,
  BlogHeroSerifSplit,
  type BlogHeroProps,
  type BlogHeroVariant,
} from './blog/BlogHeroVariants';

// — Wiki blocks (WikiSource adapter) ——————————————————————————————————
export { WikiTreeBlock, type WikiTreeBlockProps } from './wiki/WikiTreeBlock';
export { WikiBreadcrumbs, type WikiBreadcrumbsProps } from './wiki/WikiBreadcrumbs';
export { WikiPageMeta, type WikiPageMetaProps } from './wiki/WikiPageMeta';
export { WikiToc, type WikiTocProps } from './wiki/WikiToc';
export { WikiPrevNext, type WikiPrevNextProps } from './wiki/WikiPrevNext';
export { WikiSearchResults, type WikiSearchResultsProps } from './wiki/WikiSearchResults';

// — Help center blocks (anonymous portal endpoints) ——————————————————
export {
  HelpArticleFeedbackForm,
  type HelpArticleFeedbackFormProps,
} from './help-center/HelpArticleFeedbackForm';
export { HelpArticleSearch, type HelpArticleSearchProps } from './help-center/HelpArticleSearch';

// — Docs blocks (API documentation explorer) ——————————————————————————
export { SwaggerEmbed, type SwaggerEmbedProps } from './docs/SwaggerEmbed';

// — Tube blocks (Cinema8-backed video catalog; adapter wiring TBD) ————
export { VideoCarousel, type VideoCarouselProps } from './tube/VideoCarousel';
export { useTubeVideos, type TubeVideo, type UseTubeVideosOpts } from './tube/useTubeVideos';

// — LMS grid blocks (LearningSource adapter) —————————————————————————
export {
  CourseGrid,
  type CourseGridProps,
  type CourseGridCardStyle,
  type CourseGridSortOption,
} from './grid/CourseGrid';

// — LMS rail blocks (LearningSource adapter) —————————————————————————
export { CourseRow, type CourseRowProps, type CourseCardStyle } from './rail/CourseRow';
export { CourseRowTabs, type CourseRowTabsProps, type CourseRowTab } from './rail/CourseRowTabs';
export { CampsAndCompetitions, type CampsAndCompetitionsProps } from './rail/CampsAndCompetitions';
export { Top10Row, type Top10RowProps } from './rail/Top10Row';
export { AutoScrollMarquee, type AutoScrollMarqueeProps } from './rail/AutoScrollMarquee';
export {
  CategoryCarousel,
  type CategoryCarouselProps,
  type CategoryFeedSource,
  type CategoryItem,
} from './rail/CategoryCarousel';
export { LiveNowStrip, type LiveNowStripProps, type LiveItemInput } from './rail/LiveNowStrip';
export { ShortsRail, type ShortsRailProps } from './rail/ShortsRail';
export { VideoRow, type VideoRowProps } from './rail/VideoRow';
export {
  CategorySliderBlock,
  type CategorySliderBlockProps,
  type CategorySliderScrollMode,
  type CategorySliderSize,
  type CategorySliderItem,
} from './rail/CategorySliderBlock';

// — Video module blocks (MediaSource adapter) ——————————————————————————
export { VideoModuleRail, type VideoModuleRailProps } from './video/VideoModuleRail';
export { VideoModuleGrid, type VideoModuleGridProps } from './video/VideoModuleGrid';
export { VideoModuleHero, type VideoModuleHeroProps } from './video/VideoModuleHero';
export { SeriesModuleRail, type SeriesModuleRailProps } from './video/SeriesModuleRail';
export { ShortsFeed, type ShortsFeedProps, type ShortsItem } from './video/ShortsFeed';
export { ShortsCard, type ShortsCardProps } from './video/ShortsCard';

// — Discovery blocks (LearningSource / BlogSource / MediaSource) ——————
export {
  CategoryFilterPills,
  type CategoryFilterPillsProps,
} from './discovery/CategoryFilterPills';
export { WikiTreeNav, type WikiTreeNavProps } from './discovery/WikiTreeNav';
export { CategoryTree, type CategoryTreeProps } from './discovery/CategoryTree';
export {
  FullHeightDiscovery,
  type FullHeightDiscoveryProps,
  type FullHeightDiscoveryCard,
} from './discovery/FullHeightDiscovery';

// — Learning blocks (mostly prop-driven; RankedShowcase uses MediaSource) ——
export { LearningPathV2, type LearningPathV2Props } from './learning/LearningPathV2';
export { RankedShowcase, type RankedShowcaseProps } from './learning/RankedShowcase';

// — User-state blocks (auth-aware, UserResourceSource adapter) ————————
export { LastCourse, type LastCourseProps } from './user-state/LastCourse';
// The LastCourse tile on its own — so other blocks (journey detail's
// "continue" slot) render the exact same item instead of a lookalike.
export {
  LastCourseCard,
  LastCourseCardSkeleton,
  LastCourseCardStyles,
  lastCourseCardCss,
  type LastCourseCardProps,
} from './user-state/LastCourseCard';
export {
  NotificationsTimeline,
  type NotificationsTimelineProps,
} from './user-state/NotificationsTimeline';

export {
  PendingEvaluationFormsSlider,
  type PendingEvaluationFormsSliderProps,
} from './user-state/PendingEvaluationFormsSlider';

// — Micro blocks (MicroSource adapter) —————————————————————————————————
export { MicroContentRail, type MicroContentRailProps } from './micro/MicroContentRail';
export { MicroContentGrid, type MicroContentGridProps } from './micro/MicroContentGrid';
export { MicroContentItem, type MicroContentItemProps } from './micro/MicroContentItem';
export { MicroContentTypeGrid, type MicroContentTypeGridProps } from './micro/MicroContentTypeGrid';
export { MicroFavoritesRail, type MicroFavoritesRailProps } from './micro/MicroFavoritesRail';
export {
  MicroRecommendationRail,
  type MicroRecommendationRailProps,
} from './micro/MicroRecommendationRail';
export { MicroStatsStrip, type MicroStatsStripProps } from './micro/MicroStatsStrip';
export { MicroResumeRefresher, type MicroResumeRefresherProps } from './micro/MicroResumeRefresher';
export { MicroContentPopup, MicroPopupHost, useMicroPopup } from './micro/popup';

// — Islands (UserResourceSource / LearningSource.enrollments) ————————
// Auth-aware client-only widgets mounted inside system pages and
// custom blocks. NOT registered in genericBlocks — they're consumed
// primitives, not droppable.
export * from './islands';

// — Micro popup players (MicroSource.content.fetchById + progress) ——
// Modal/popup machinery for the micro-content experience. Mount the
// {@link MicroContentPopup} once per page and feed it the item the
// user clicked.
export * from './micro/popup';
export { MicroPromptCard, type MicroPromptCardProps } from './micro/MicroPromptCard';
export { MicroPostLessonCheck, type MicroPostLessonCheckProps } from './micro/MicroPostLessonCheck';
export { LessonNavWithCheck, type LessonNavWithCheckProps } from './micro/LessonNavWithCheck';
export {
  MicroCohortDiscussModal,
  type MicroCohortDiscussModalProps,
} from './micro/MicroCohortDiscussModal';

// — System page layouts (BlogDetail, WikiDetail/Index, VideoDetail/Index,
//   Series, Channel, Library, MicroListing) ————————————————————————————
// Whole-page surfaces that host routes mount directly. Each consumes
// one or more runtime adapters. Theme bundles override via the
// system/primitives map.
export * from './system';
export { resolvePaddingStyle, resolveInsetStyle, type PaddingBox } from './_shared/resolvePadding';
export { surfaceBackground, surfaceStyle, type SurfaceToken } from './_shared/surface';
export { resizeImageUrl } from './_shared/resizeImageUrl';

// — Aggregate registry ————————————————————————————————————————————————
import dynamic from 'next/dynamic';
import type { BlockComponent } from '@akademi/site-builder-runtime';
const HeroBanner = dynamic(() =>
  import('./hero/HeroBanner').then((m) => ({ default: m.HeroBanner })),
);
const HeroSplit = dynamic(() => import('./hero/HeroSplit').then((m) => ({ default: m.HeroSplit })));
const HeroEditorial = dynamic(() =>
  import('./hero/HeroEditorial').then((m) => ({ default: m.HeroEditorial })),
);
const HeroSlider = dynamic(() =>
  import('./hero/HeroSlider').then((m) => ({ default: m.HeroSlider })),
);
const HeroCinematic = dynamic(() =>
  import('./hero/HeroCinematic').then((m) => ({ default: m.HeroCinematic })),
);
const HeroSpotlightCards = dynamic(() =>
  import('./hero/HeroSpotlightCards').then((m) => ({ default: m.HeroSpotlightCards })),
);
const HeroSpotlight = dynamic(() =>
  import('./hero/HeroSpotlight').then((m) => ({ default: m.HeroSpotlight })),
);
const VideoPlayerHero = dynamic(() =>
  import('./hero/VideoPlayerHero').then((m) => ({ default: m.VideoPlayerHero })),
);
const HeroFeatureRail = dynamic(() =>
  import('./hero/HeroFeatureRail').then((m) => ({ default: m.HeroFeatureRail })),
);
const HeroCarousel = dynamic(() =>
  import('./hero/HeroCarousel').then((m) => ({ default: m.HeroCarousel })),
);
const HeroCategoryBanner = dynamic(() =>
  import('./hero/HeroCategoryBanner').then((m) => ({ default: m.HeroCategoryBanner })),
);
const HeroDualBanner = dynamic(() =>
  import('./hero/HeroDualBanner').then((m) => ({ default: m.HeroDualBanner })),
);
const HeroSplitRotator = dynamic(() =>
  import('./hero/HeroSplitRotator').then((m) => ({ default: m.HeroSplitRotator })),
);
const HeroSignalDeck = dynamic(() =>
  import('./hero/HeroSignalDeck').then((m) => ({ default: m.HeroSignalDeck })),
);
const HeroPromoRail = dynamic(() =>
  import('./hero/HeroPromoRail').then((m) => ({ default: m.HeroPromoRail })),
);
const HeroTriColumn = dynamic(() =>
  import('./hero/HeroTriColumn').then((m) => ({ default: m.HeroTriColumn })),
);
const HeroPlaylistRail = dynamic(() =>
  import('./hero/HeroPlaylistRail').then((m) => ({ default: m.HeroPlaylistRail })),
);
const HeroFullHeightBanner = dynamic(() =>
  import('./hero/HeroFullHeightBanner').then((m) => ({ default: m.HeroFullHeightBanner })),
);
const PluralsightHero = dynamic(() =>
  import('./hero/PluralsightHero').then((m) => ({ default: m.PluralsightHero })),
);
const WildTvHero = dynamic(() =>
  import('./hero/WildTvHero').then((m) => ({ default: m.WildTvHero })),
);
const ActivityRow = dynamic(() =>
  import('./activity/ActivityRow').then((m) => ({ default: m.ActivityRow })),
);
const ActivityBlock = dynamic(() =>
  import('./activity/ActivityBlock').then((m) => ({ default: m.ActivityBlock })),
);
const ActivityBlockBtk = dynamic(() =>
  import('./activity/ActivityBlockBtk').then((m) => ({ default: m.ActivityBlockBtk })),
);
const DynamicFormBlock = dynamic(() =>
  import('./form/DynamicFormBlock').then((m) => ({ default: m.DynamicFormBlock })),
);
const CtaBand = dynamic(() => import('./cta/CtaBand').then((m) => ({ default: m.CtaBand })));
const CtaCardModern = dynamic(() =>
  import('./cta/CtaCardModern').then((m) => ({ default: m.CtaCardModern })),
);
const SubscribeCard = dynamic(() =>
  import('./cta/SubscribeCard').then((m) => ({ default: m.SubscribeCard })),
);
const RichText = dynamic(() => import('./text/RichText').then((m) => ({ default: m.RichText })));
const HtmlBlock = dynamic(() => import('./html/HtmlBlock').then((m) => ({ default: m.HtmlBlock })));
const FaqAccordion = dynamic(() =>
  import('./text/FaqAccordion').then((m) => ({ default: m.FaqAccordion })),
);
const Image = dynamic(() => import('./media/Image').then((m) => ({ default: m.Image })));
const MediaCardRow = dynamic(() =>
  import('./media/MediaCardRow').then((m) => ({ default: m.MediaCardRow })),
);
const Podcasts = dynamic(() => import('./media/Podcasts').then((m) => ({ default: m.Podcasts })));
const FullHeightPodcast = dynamic(() =>
  import('./media/FullHeightPodcast').then((m) => ({ default: m.FullHeightPodcast })),
);
const AcmePartnerBadge = dynamic(() =>
  import('./misc/AcmePartnerBadge').then((m) => ({ default: m.AcmePartnerBadge })),
);
const Band = dynamic(() => import('./layout/Band').then((m) => ({ default: m.Band })));
const BackgroundGlow = dynamic(() =>
  import('./layout/BackgroundGlow').then((m) => ({ default: m.BackgroundGlow })),
);
const Columns = dynamic(() => import('./layout/Columns').then((m) => ({ default: m.Columns })));
const Container = dynamic(() =>
  import('./layout/Container').then((m) => ({ default: m.Container })),
);
const Divider = dynamic(() => import('./layout/Divider').then((m) => ({ default: m.Divider })));
const Spacer = dynamic(() => import('./layout/Spacer').then((m) => ({ default: m.Spacer })));
const FeatureGrid = dynamic(() =>
  import('./grid/FeatureGrid').then((m) => ({ default: m.FeatureGrid })),
);
const PartnersGrid = dynamic(() =>
  import('./grid/PartnersGrid').then((m) => ({ default: m.PartnersGrid })),
);
const CategoryGrid = dynamic(() =>
  import('./grid/CategoryGrid').then((m) => ({ default: m.CategoryGrid })),
);
const PillarStack = dynamic(() =>
  import('./grid/PillarStack').then((m) => ({ default: m.PillarStack })),
);
const MarketplaceCatalogGrid = dynamic(() =>
  import('./marketplace/MarketplaceCatalogGrid').then((m) => ({
    default: m.MarketplaceCatalogGrid,
  })),
);
const MarketplaceCourseGrid = dynamic(() =>
  import('./marketplace/MarketplaceCourseGrid').then((m) => ({ default: m.MarketplaceCourseGrid })),
);
const MarketplaceCoursePurchaseBox = dynamic(() =>
  import('./marketplace/MarketplaceCoursePurchaseBox').then((m) => ({
    default: m.MarketplaceCoursePurchaseBox,
  })),
);
const MarketplaceCatalogHero = dynamic(() =>
  import('./marketplace/MarketplaceCatalogHero').then((m) => ({
    default: m.MarketplaceCatalogHero,
  })),
);
const MarketplaceCartItems = dynamic(() =>
  import('./marketplace/MarketplaceCartItems').then((m) => ({ default: m.MarketplaceCartItems })),
);
const MarketplaceCartSummary = dynamic(() =>
  import('./marketplace/MarketplaceCartSummary').then((m) => ({
    default: m.MarketplaceCartSummary,
  })),
);
const MarketplaceCouponCode = dynamic(() =>
  import('./marketplace/MarketplaceCouponCode').then((m) => ({ default: m.MarketplaceCouponCode })),
);
const MarketplaceAddressSelect = dynamic(() =>
  import('./marketplace/MarketplaceAddressSelect').then((m) => ({
    default: m.MarketplaceAddressSelect,
  })),
);
const MarketplacePaymentMethod = dynamic(() =>
  import('./marketplace/MarketplacePaymentMethod').then((m) => ({
    default: m.MarketplacePaymentMethod,
  })),
);
const MarketplaceCheckoutCta = dynamic(() =>
  import('./marketplace/MarketplaceCheckoutCta').then((m) => ({
    default: m.MarketplaceCheckoutCta,
  })),
);
const MarketplaceCheckoutStepper = dynamic(() =>
  import('./marketplace/MarketplaceCheckoutStepper').then((m) => ({
    default: m.MarketplaceCheckoutStepper,
  })),
);
const MarketplacePendingOrders = dynamic(() =>
  import('./marketplace/MarketplacePendingOrders').then((m) => ({
    default: m.MarketplacePendingOrders,
  })),
);
const MarketplaceBankTransferInstructions = dynamic(() =>
  import('./marketplace/MarketplaceBankTransferInstructions').then((m) => ({
    default: m.MarketplaceBankTransferInstructions,
  })),
);
const MetricRow = dynamic(() =>
  import('./stats/MetricRow').then((m) => ({ default: m.MetricRow })),
);
const FullHeightStats = dynamic(() =>
  import('./stats/FullHeightStats').then((m) => ({ default: m.FullHeightStats })),
);
const GamificationPanel = dynamic(() =>
  import('./gamification/GamificationPanel').then((m) => ({ default: m.GamificationPanel })),
);
const GamificationPanelV2 = dynamic(() =>
  import('./gamification/GamificationPanelV2').then((m) => ({ default: m.GamificationPanelV2 })),
);
const GamificationPanelV3 = dynamic(() =>
  import('./gamification/GamificationPanelV3').then((m) => ({ default: m.GamificationPanelV3 })),
);
const FullBadge = dynamic(() =>
  import('./gamification/FullBadge').then((m) => ({ default: m.FullBadge })),
);
const ComingSoon = dynamic(() =>
  import('./promo/ComingSoon').then((m) => ({ default: m.ComingSoon })),
);
const AdBanner = dynamic(() => import('./promo/AdBanner').then((m) => ({ default: m.AdBanner })));
const MobileAppPromo = dynamic(() =>
  import('./promo/MobileAppPromo').then((m) => ({ default: m.MobileAppPromo })),
);
const MobileAppShowcase = dynamic(() =>
  import('./promo/MobileAppShowcase').then((m) => ({ default: m.MobileAppShowcase })),
);
const FullHeightMobilePromo = dynamic(() =>
  import('./promo/FullHeightMobilePromo').then((m) => ({ default: m.FullHeightMobilePromo })),
);
const ButtonGroup = dynamic(() =>
  import('./tabs/ButtonGroup').then((m) => ({ default: m.ButtonGroup })),
);
const PlansPricing = dynamic(() =>
  import('./nav/PlansPricing').then((m) => ({ default: m.PlansPricing })),
);
const CategoryMenu = dynamic(() =>
  import('./nav/CategoryMenu').then((m) => ({ default: m.CategoryMenu })),
);
const Testimonials = dynamic(() =>
  import('./proof/Testimonials').then((m) => ({ default: m.Testimonials })),
);
const TestimonialsBento = dynamic(() =>
  import('./proof/TestimonialsBento').then((m) => ({ default: m.TestimonialsBento })),
);
const GenericLearningJourneysImpl = dynamic(() =>
  import('./journeys/LearningJourneys').then((m) => ({ default: m.LearningJourneys })),
);
const LearningJourneyPlans = dynamic(() =>
  import('./journeys/LearningJourneyPlans').then((m) => ({ default: m.LearningJourneyPlans })),
);
const HeroVideo = dynamic(() =>
  import('./hero-video/HeroVideo').then((m) => ({ default: m.HeroVideo })),
);
const HeroVideoBackgroundHero = dynamic(() =>
  import('./hero-video/HeroVideoBackground').then((m) => ({ default: m.HeroVideoBackgroundHero })),
);
const CalendarBox = dynamic(() =>
  import('./calendar/CalendarBox').then((m) => ({ default: m.CalendarBox })),
);
const LiveCalendarBox = dynamic(() =>
  import('./calendar/LiveCalendarBox').then((m) => ({ default: m.LiveCalendarBox })),
);
const CalendlyBlock = dynamic(() =>
  import('./embeds/CalendlyBlock').then((m) => ({ default: m.CalendlyBlock })),
);
const TypeformBlock = dynamic(() =>
  import('./embeds/TypeformBlock').then((m) => ({ default: m.TypeformBlock })),
);
const HubSpotMeetingsBlock = dynamic(() =>
  import('./embeds/HubSpotMeetingsBlock').then((m) => ({ default: m.HubSpotMeetingsBlock })),
);
const HubSpotFormBlock = dynamic(() =>
  import('./embeds/HubSpotFormBlock').then((m) => ({ default: m.HubSpotFormBlock })),
);
const EmbeddedVideo = dynamic(() =>
  import('./embeds/EmbeddedVideo').then((m) => ({ default: m.EmbeddedVideo })),
);
const BlogArticleGrid = dynamic(() =>
  import('./blog/BlogArticleGrid').then((m) => ({ default: m.BlogArticleGrid })),
);
const BlogFeaturedHero = dynamic(() =>
  import('./blog/BlogFeaturedHero').then((m) => ({ default: m.BlogFeaturedHero })),
);
const WikiTreeBlock = dynamic(() =>
  import('./wiki/WikiTreeBlock').then((m) => ({ default: m.WikiTreeBlock })),
);
const WikiBreadcrumbs = dynamic(() =>
  import('./wiki/WikiBreadcrumbs').then((m) => ({ default: m.WikiBreadcrumbs })),
);
const WikiPageMeta = dynamic(() =>
  import('./wiki/WikiPageMeta').then((m) => ({ default: m.WikiPageMeta })),
);
const WikiToc = dynamic(() => import('./wiki/WikiToc').then((m) => ({ default: m.WikiToc })));
const WikiPrevNext = dynamic(() =>
  import('./wiki/WikiPrevNext').then((m) => ({ default: m.WikiPrevNext })),
);
const WikiSearchResults = dynamic(() =>
  import('./wiki/WikiSearchResults').then((m) => ({ default: m.WikiSearchResults })),
);
const HelpArticleFeedbackForm = dynamic(() =>
  import('./help-center/HelpArticleFeedbackForm').then((m) => ({
    default: m.HelpArticleFeedbackForm,
  })),
);
const HelpArticleSearch = dynamic(() =>
  import('./help-center/HelpArticleSearch').then((m) => ({ default: m.HelpArticleSearch })),
);
const SwaggerEmbed = dynamic(() =>
  import('./docs/SwaggerEmbed').then((m) => ({ default: m.SwaggerEmbed })),
);
const VideoCarousel = dynamic(() =>
  import('./tube/VideoCarousel').then((m) => ({ default: m.VideoCarousel })),
);
const CourseGrid = dynamic(() =>
  import('./grid/CourseGrid').then((m) => ({ default: m.CourseGrid })),
);
const CourseRow = dynamic(() => import('./rail/CourseRow').then((m) => ({ default: m.CourseRow })));
const CourseRowTabs = dynamic(() =>
  import('./rail/CourseRowTabs').then((m) => ({ default: m.CourseRowTabs })),
);
const CampsAndCompetitions = dynamic(() =>
  import('./rail/CampsAndCompetitions').then((m) => ({ default: m.CampsAndCompetitions })),
);
const Top10Row = dynamic(() => import('./rail/Top10Row').then((m) => ({ default: m.Top10Row })));
const AutoScrollMarquee = dynamic(() =>
  import('./rail/AutoScrollMarquee').then((m) => ({ default: m.AutoScrollMarquee })),
);
const CategoryCarousel = dynamic(() =>
  import('./rail/CategoryCarousel').then((m) => ({ default: m.CategoryCarousel })),
);
const LiveNowStrip = dynamic(() =>
  import('./rail/LiveNowStrip').then((m) => ({ default: m.LiveNowStrip })),
);
const ShortsRail = dynamic(() =>
  import('./rail/ShortsRail').then((m) => ({ default: m.ShortsRail })),
);
const VideoRow = dynamic(() => import('./rail/VideoRow').then((m) => ({ default: m.VideoRow })));
const CategorySliderBlock = dynamic(() =>
  import('./rail/CategorySliderBlock').then((m) => ({ default: m.CategorySliderBlock })),
);
const VideoModuleRail = dynamic(() =>
  import('./video/VideoModuleRail').then((m) => ({ default: m.VideoModuleRail })),
);
const VideoModuleGrid = dynamic(() =>
  import('./video/VideoModuleGrid').then((m) => ({ default: m.VideoModuleGrid })),
);
const VideoModuleHero = dynamic(() =>
  import('./video/VideoModuleHero').then((m) => ({ default: m.VideoModuleHero })),
);
const SeriesModuleRail = dynamic(() =>
  import('./video/SeriesModuleRail').then((m) => ({ default: m.SeriesModuleRail })),
);
const ShortsFeed = dynamic(() =>
  import('./video/ShortsFeed').then((m) => ({ default: m.ShortsFeed })),
);
const CategoryFilterPills = dynamic(() =>
  import('./discovery/CategoryFilterPills').then((m) => ({ default: m.CategoryFilterPills })),
);
const WikiTreeNav = dynamic(() =>
  import('./discovery/WikiTreeNav').then((m) => ({ default: m.WikiTreeNav })),
);
const CategoryTree = dynamic(() =>
  import('./discovery/CategoryTree').then((m) => ({ default: m.CategoryTree })),
);
const FullHeightDiscovery = dynamic(() =>
  import('./discovery/FullHeightDiscovery').then((m) => ({ default: m.FullHeightDiscovery })),
);
const LearningPathV2 = dynamic(() =>
  import('./learning/LearningPathV2').then((m) => ({ default: m.LearningPathV2 })),
);
const RankedShowcase = dynamic(() =>
  import('./learning/RankedShowcase').then((m) => ({ default: m.RankedShowcase })),
);
const LastCourse = dynamic(() =>
  import('./user-state/LastCourse').then((m) => ({ default: m.LastCourse })),
);
const NotificationsTimeline = dynamic(() =>
  import('./user-state/NotificationsTimeline').then((m) => ({ default: m.NotificationsTimeline })),
);
const PendingEvaluationFormsSlider = dynamic(() =>
  import('./user-state/PendingEvaluationFormsSlider').then((m) => ({
    default: m.PendingEvaluationFormsSlider,
  })),
);
const MicroContentRail = dynamic(() =>
  import('./micro/MicroContentRail').then((m) => ({ default: m.MicroContentRail })),
);
const MicroContentItem = dynamic(() =>
  import('./micro/MicroContentItem').then((m) => ({ default: m.MicroContentItem })),
);
const MicroContentGrid = dynamic(() =>
  import('./micro/MicroContentGrid').then((m) => ({ default: m.MicroContentGrid })),
);
const MicroContentTypeGrid = dynamic(() =>
  import('./micro/MicroContentTypeGrid').then((m) => ({ default: m.MicroContentTypeGrid })),
);
const MicroFavoritesRail = dynamic(() =>
  import('./micro/MicroFavoritesRail').then((m) => ({ default: m.MicroFavoritesRail })),
);
const MicroRecommendationRail = dynamic(() =>
  import('./micro/MicroRecommendationRail').then((m) => ({ default: m.MicroRecommendationRail })),
);
const MicroStatsStrip = dynamic(() =>
  import('./micro/MicroStatsStrip').then((m) => ({ default: m.MicroStatsStrip })),
);
const MicroResumeRefresher = dynamic(() =>
  import('./micro/MicroResumeRefresher').then((m) => ({ default: m.MicroResumeRefresher })),
);
// System layouts used as block types by the content-module page layouts
// (micro-kind, video-detail, video-series-*, video-channel-*). They fetch
// client-side via the Micro/Media adapters; route params arrive as props
// via the portal's bindPathParams.
const MicroListingLayout = dynamic(() =>
  import('./system/MicroListingLayout').then((m) => ({ default: m.MicroListingLayout })),
);
const VideoDetailLayout = dynamic(() =>
  import('./system/VideoDetailLayout').then((m) => ({ default: m.VideoDetailLayout })),
);
const CommentsBlock = dynamic(() =>
  import('./system/CommentsBlock').then((m) => ({ default: m.CommentsBlock })),
);
const ChannelDetailLayout = dynamic(() =>
  import('./system/ChannelDetailLayout').then((m) => ({ default: m.ChannelDetailLayout })),
);
const ChannelDiscoveryLayout = dynamic(() =>
  import('./system/ChannelDiscoveryLayout').then((m) => ({ default: m.ChannelDiscoveryLayout })),
);
const SeriesDetailLayout = dynamic(() =>
  import('./system/SeriesDetailLayout').then((m) => ({ default: m.SeriesDetailLayout })),
);
const SeriesPlayerLayout = dynamic(() =>
  import('./system/SeriesPlayerLayout').then((m) => ({ default: m.SeriesPlayerLayout })),
);
const LibraryClient = dynamic(() =>
  import('./system/LibraryClient').then((m) => ({ default: m.LibraryClient })),
);
// Invisible history marker — VideoDetailLayout mounts it on load; registered
// so operators can also place it explicitly on custom watch pages.
const HistoryWriter = dynamic(() =>
  import('./system/HistoryWriter').then((m) => ({ default: m.HistoryWriter })),
);
const PluralsightTopicGrid = dynamic(() =>
  import('./grid/PluralsightTopicGrid').then((m) => ({ default: m.PluralsightTopicGrid })),
);
const PluralsightOutcomeGrid = dynamic(() =>
  import('./grid/PluralsightOutcomeGrid').then((m) => ({ default: m.PluralsightOutcomeGrid })),
);
const PluralsightProofBand = dynamic(() =>
  import('./stats/PluralsightProofBand').then((m) => ({ default: m.PluralsightProofBand })),
);

/**
 * Drop-in map for {@code createBlockResolver({ defaultBlocks: genericBlocks })}.
 * Maps the canonical block-type id to the component implementing it.
 * Block-type ids match the backend's {@code BlockDefinition.type}
 * seeded into the registry.
 */
export const genericBlocks: Record<string, BlockComponent> = {
  // Hero variants
  HeroBanner: HeroBanner as unknown as BlockComponent,
  HeroSplit: HeroSplit as unknown as BlockComponent,
  HeroEditorial: HeroEditorial as unknown as BlockComponent,
  HeroSlider: HeroSlider as unknown as BlockComponent,
  HeroCinematic: HeroCinematic as unknown as BlockComponent,
  HeroSpotlightCards: HeroSpotlightCards as unknown as BlockComponent,
  HeroSpotlight: HeroSpotlight as unknown as BlockComponent,
  VideoPlayerHero: VideoPlayerHero as unknown as BlockComponent,
  HeroFeatureRail: HeroFeatureRail as unknown as BlockComponent,
  HeroCarousel: HeroCarousel as unknown as BlockComponent,
  HeroCategoryBanner: HeroCategoryBanner as unknown as BlockComponent,
  HeroDualBanner: HeroDualBanner as unknown as BlockComponent,
  HeroSplitRotator: HeroSplitRotator as unknown as BlockComponent,
  HeroSignalDeck: HeroSignalDeck as unknown as BlockComponent,
  HeroPromoRail: HeroPromoRail as unknown as BlockComponent,
  HeroTriColumn: HeroTriColumn as unknown as BlockComponent,
  HeroPlaylistRail: HeroPlaylistRail as unknown as BlockComponent,
  HeroFullHeightBanner: HeroFullHeightBanner as unknown as BlockComponent,
  // Full-height Stats / Discovery / Podcast shipped as Hero* types before
  // they moved out of the hero family. The old names stay registered so a
  // page saved under them keeps rendering; the block picker only offers
  // the new names (seed-data/blocks/FullHeight*.json).
  HeroFullHeightStats: FullHeightStats as unknown as BlockComponent,
  HeroFullHeightDiscovery: FullHeightDiscovery as unknown as BlockComponent,
  HeroFullHeightPodcast: FullHeightPodcast as unknown as BlockComponent,
  PluralsightHero: PluralsightHero as unknown as BlockComponent,
  WildTvHero: WildTvHero as unknown as BlockComponent,
  // Text + FAQ
  RichText: RichText as unknown as BlockComponent,
  HtmlBlock: HtmlBlock as unknown as BlockComponent,
  FaqAccordion: FaqAccordion as unknown as BlockComponent,
  // Activity / events
  ActivityRow: ActivityRow as unknown as BlockComponent,
  ActivityBlock: ActivityBlock as unknown as BlockComponent,
  ActivityBlockBtk: ActivityBlockBtk as unknown as BlockComponent,
  // Forms
  DynamicFormBlock: DynamicFormBlock as unknown as BlockComponent,
  // CTAs
  CtaBand: CtaBand as unknown as BlockComponent,
  CtaCardModern: CtaCardModern as unknown as BlockComponent,
  SubscribeCard: SubscribeCard as unknown as BlockComponent,
  // Media
  Image: Image as unknown as BlockComponent,
  MediaCardRow: MediaCardRow as unknown as BlockComponent,
  Podcasts: Podcasts as unknown as BlockComponent,
  FullHeightPodcast: FullHeightPodcast as unknown as BlockComponent,
  // Layout primitives
  Band: Band as unknown as BlockComponent,
  BackgroundGlow: BackgroundGlow as unknown as BlockComponent,
  Columns: Columns as unknown as BlockComponent,
  Container: Container as unknown as BlockComponent,
  Divider: Divider as unknown as BlockComponent,
  Spacer: Spacer as unknown as BlockComponent,
  // Grids
  FeatureGrid: FeatureGrid as unknown as BlockComponent,
  PartnersGrid: PartnersGrid as unknown as BlockComponent,
  CategoryGrid: CategoryGrid as unknown as BlockComponent,
  PillarStack: PillarStack as unknown as BlockComponent,
  // Marketplace
  MarketplaceCatalogGrid: MarketplaceCatalogGrid as unknown as BlockComponent,
  MarketplaceCourseGrid: MarketplaceCourseGrid as unknown as BlockComponent,
  MarketplaceCoursePurchaseBox: MarketplaceCoursePurchaseBox as unknown as BlockComponent,
  MarketplaceCatalogHero: MarketplaceCatalogHero as unknown as BlockComponent,
  MarketplaceCartItems: MarketplaceCartItems as unknown as BlockComponent,
  MarketplaceCartSummary: MarketplaceCartSummary as unknown as BlockComponent,
  MarketplaceCouponCode: MarketplaceCouponCode as unknown as BlockComponent,
  MarketplaceAddressSelect: MarketplaceAddressSelect as unknown as BlockComponent,
  MarketplacePaymentMethod: MarketplacePaymentMethod as unknown as BlockComponent,
  MarketplaceCheckoutCta: MarketplaceCheckoutCta as unknown as BlockComponent,
  MarketplaceCheckoutStepper: MarketplaceCheckoutStepper as unknown as BlockComponent,
  MarketplacePendingOrders: MarketplacePendingOrders as unknown as BlockComponent,
  MarketplaceBankTransferInstructions:
    MarketplaceBankTransferInstructions as unknown as BlockComponent,
  // Stats
  MetricRow: MetricRow as unknown as BlockComponent,
  FullHeightStats: FullHeightStats as unknown as BlockComponent,
  // Gamification
  GamificationPanel: GamificationPanel as unknown as BlockComponent,
  GamificationPanelV2: GamificationPanelV2 as unknown as BlockComponent,
  GamificationPanelV3: GamificationPanelV3 as unknown as BlockComponent,
  FullBadge: FullBadge as unknown as BlockComponent,
  // Promo
  ComingSoon: ComingSoon as unknown as BlockComponent,
  AdBanner: AdBanner as unknown as BlockComponent,
  MobileAppPromo: MobileAppPromo as unknown as BlockComponent,
  MobileAppShowcase: MobileAppShowcase as unknown as BlockComponent,
  FullHeightMobilePromo: FullHeightMobilePromo as unknown as BlockComponent,
  // Tabs
  ButtonGroup: ButtonGroup as unknown as BlockComponent,
  // Nav
  PlansPricing: PlansPricing as unknown as BlockComponent,
  CategoryMenu: CategoryMenu as unknown as BlockComponent,
  // Misc / proof / journeys / video hero
  AcmePartnerBadge: AcmePartnerBadge as unknown as BlockComponent,
  Testimonials: Testimonials as unknown as BlockComponent,
  TestimonialsBento: TestimonialsBento as unknown as BlockComponent,
  LearningJourneys: GenericLearningJourneysImpl as unknown as BlockComponent,
  LearningJourneyPlans: LearningJourneyPlans as unknown as BlockComponent,
  HeroVideo: HeroVideo as unknown as BlockComponent,
  HeroVideoBackground: HeroVideoBackgroundHero as unknown as BlockComponent,
  // Calendar
  CalendarBox: CalendarBox as unknown as BlockComponent,
  LiveCalendarBox: LiveCalendarBox as unknown as BlockComponent,
  // Third-party embeds
  CalendlyBlock: CalendlyBlock as unknown as BlockComponent,
  TypeformBlock: TypeformBlock as unknown as BlockComponent,
  HubSpotMeetingsBlock: HubSpotMeetingsBlock as unknown as BlockComponent,
  HubSpotFormBlock: HubSpotFormBlock as unknown as BlockComponent,
  EmbeddedVideo: EmbeddedVideo as unknown as BlockComponent,
  // Blog (BlogSource adapter)
  BlogArticleGrid: BlogArticleGrid as unknown as BlockComponent,
  BlogFeaturedHero: BlogFeaturedHero as unknown as BlockComponent,
  // Wiki (WikiSource adapter)
  WikiTreeBlock: WikiTreeBlock as unknown as BlockComponent,
  WikiBreadcrumbs: WikiBreadcrumbs as unknown as BlockComponent,
  WikiPageMeta: WikiPageMeta as unknown as BlockComponent,
  WikiToc: WikiToc as unknown as BlockComponent,
  WikiPrevNext: WikiPrevNext as unknown as BlockComponent,
  WikiSearchResults: WikiSearchResults as unknown as BlockComponent,
  // Help center (anonymous portal endpoints)
  HelpArticleFeedbackForm: HelpArticleFeedbackForm as unknown as BlockComponent,
  HelpArticleSearch: HelpArticleSearch as unknown as BlockComponent,
  // Docs (API documentation explorer)
  SwaggerEmbed: SwaggerEmbed as unknown as BlockComponent,
  // Tube (Cinema8-backed video catalog; adapter wiring TBD)
  VideoCarousel: VideoCarousel as unknown as BlockComponent,
  // LMS grids (LearningSource adapter)
  CourseGrid: CourseGrid as unknown as BlockComponent,
  // LMS rails (LearningSource adapter)
  CourseRow: CourseRow as unknown as BlockComponent,
  CourseRowTabs: CourseRowTabs as unknown as BlockComponent,
  CampsAndCompetitions: CampsAndCompetitions as unknown as BlockComponent,
  Top10Row: Top10Row as unknown as BlockComponent,
  AutoScrollMarquee: AutoScrollMarquee as unknown as BlockComponent,
  CategoryCarousel: CategoryCarousel as unknown as BlockComponent,
  CategorySliderBlock: CategorySliderBlock as unknown as BlockComponent,
  // Media (MediaSource adapter)
  LiveNowStrip: LiveNowStrip as unknown as BlockComponent,
  ShortsRail: ShortsRail as unknown as BlockComponent,
  VideoRow: VideoRow as unknown as BlockComponent,
  VideoModuleRail: VideoModuleRail as unknown as BlockComponent,
  VideoModuleGrid: VideoModuleGrid as unknown as BlockComponent,
  VideoModuleHero: VideoModuleHero as unknown as BlockComponent,
  SeriesModuleRail: SeriesModuleRail as unknown as BlockComponent,
  ShortsFeed: ShortsFeed as unknown as BlockComponent,
  // Discovery (multi-adapter)
  CategoryFilterPills: CategoryFilterPills as unknown as BlockComponent,
  WikiTreeNav: WikiTreeNav as unknown as BlockComponent,
  CategoryTree: CategoryTree as unknown as BlockComponent,
  FullHeightDiscovery: FullHeightDiscovery as unknown as BlockComponent,
  // Learning (prop-driven; RankedShowcase needs MediaSource)
  LearningPathV2: LearningPathV2 as unknown as BlockComponent,
  RankedShowcase: RankedShowcase as unknown as BlockComponent,
  // User-state (UserResourceSource adapter)
  LastCourse: LastCourse as unknown as BlockComponent,
  NotificationsTimeline: NotificationsTimeline as unknown as BlockComponent,
  PendingEvaluationFormsSlider: PendingEvaluationFormsSlider as unknown as BlockComponent,
  // Micro (MicroSource adapter)
  MicroContentRail: MicroContentRail as unknown as BlockComponent,
  MicroContentGrid: MicroContentGrid as unknown as BlockComponent,
  MicroContentItem: MicroContentItem as unknown as BlockComponent,
  MicroContentTypeGrid: MicroContentTypeGrid as unknown as BlockComponent,
  MicroFavoritesRail: MicroFavoritesRail as unknown as BlockComponent,
  MicroRecommendationRail: MicroRecommendationRail as unknown as BlockComponent,
  MicroStatsStrip: MicroStatsStrip as unknown as BlockComponent,
  MicroResumeRefresher: MicroResumeRefresher as unknown as BlockComponent,
  // System layouts referenced by content-module page layouts
  MicroListingLayout: MicroListingLayout as unknown as BlockComponent,
  VideoDetailLayout: VideoDetailLayout as unknown as BlockComponent,
  CommentsBlock: CommentsBlock as unknown as BlockComponent,
  ChannelDetailLayout: ChannelDetailLayout as unknown as BlockComponent,
  ChannelDiscoveryLayout: ChannelDiscoveryLayout as unknown as BlockComponent,
  SeriesDetailLayout: SeriesDetailLayout as unknown as BlockComponent,
  SeriesPlayerLayout: SeriesPlayerLayout as unknown as BlockComponent,
  LibraryClient: LibraryClient as unknown as BlockComponent,
  HistoryWriter: HistoryWriter as unknown as BlockComponent,
  // Pluralsight theme blocks
  PluralsightTopicGrid: PluralsightTopicGrid as unknown as BlockComponent,
  PluralsightOutcomeGrid: PluralsightOutcomeGrid as unknown as BlockComponent,
  PluralsightProofBand: PluralsightProofBand as unknown as BlockComponent,
  // Raw HTML: the dedicated `HtmlBlock` (registered above) renders
  // operator-pasted markup verbatim. `RichText` with format='raw-html'
  // remains as an equivalent escape hatch inside the text block.
};
